import {createStore} from '@stripe-internal/state-manager';
import type {
  Screen,
  FeatureFlags,
  SupportedCurrency,
  WalletOption,
  WalletSession,
  PaymentDetails,
  TransactionDetails,
  ExternalPaymentDetails,
  WalletConnection,
  SettlementPartner,
  State,
  NetworkType,
  MerchantBranding,
  MandateDetails,
  Overlay,
  ErrorMessage,
  ExchangeOption,
  AnyCryptoDeposit,
} from 'src/v2/state/types';
import {MERCHANT_BRANDING_DEFAULTS} from 'src/v2/util/merchantBrandingDefaults';

export type SessionState = {
  // Session model
  id: string;
  livemode: boolean;
  merchant: string;
  consumerAccount: string | null;
  state: State;
  settlementPartner: SettlementPartner;
  depositAddresses: Record<string, string>;
  directDepositAddresses: Record<string, string>;
  spenderContractAddresses: Record<string, string>;
  clientSecret: string;
  setupFutureUsage: boolean;
  featureFlags: FeatureFlags;
  supportedCurrencies: SupportedCurrency[];
  walletConnectSupportedCurrencies: SupportedCurrency[];
  directDepositSupportedCurrencies: SupportedCurrency[];
  walletSessions: WalletSession[];
  consumerWallet: string | null;
  disabledWalletProviders: string[];
  validBefore: string;

  // only for payin session
  paymentDetails: PaymentDetails;
  transactionDetails: TransactionDetails;
  externalPaymentDetails: ExternalPaymentDetails | null;
  hasSucceededOrProcessingSetupAttempt: boolean;

  // only for setup session
  mandateDetails: MandateDetails;

  // Other state
  screen: Screen;
  // For the most recent wallet that has attempted to connect or has connected in this session.
  // Note this doesn't always match the active wallet.
  newWallet: {
    walletOption: WalletOption;
    networkType: NetworkType | null;
  } | null;
  exchangeOption: ExchangeOption | null;
  walletConnection: WalletConnection;
  merchantBranding: MerchantBranding;
  overlay: Overlay | null;
  errorBanner: ErrorMessage | null;
  anyCryptoDeposit: AnyCryptoDeposit;
  selectedCurrency: SupportedCurrency | null;
};

export type Store = ReturnType<typeof createStore<SessionState>>;

const initialValues: SessionState = {
  // Session model
  id: '',
  livemode: false,
  merchant: '',
  consumerAccount: null,
  state: 'checkout',
  disabledWalletProviders: [],
  settlementPartner: 'paxos',
  depositAddresses: {},
  directDepositAddresses: {},
  spenderContractAddresses: {},
  clientSecret: '',
  setupFutureUsage: false,
  featureFlags: {
    useAsyncConfirmationFlow: false,
    enable_crypto_payins_pay_from_exchange: false,
    enable_crypto_payins_direct_deposit: false,
    enable_underpayment_detection: false,
    enable_gasless_v2: false,
    enable_smart_contract_wallet_detection: false,
    crypto_payins_generic_payment_title: false,
    enable_pay_with_any_crypto: false,
    enable_pay_with_any_crypto_wallet_connect: false,
    enable_pay_with_any_crypto_wallet_connect_non_stables: false,
    enable_direct_deposit_any_crypto_flow: false,
    enable_crypto_payins_dark_mode: true,
  },
  supportedCurrencies: [],
  walletConnectSupportedCurrencies: [],
  directDepositSupportedCurrencies: [],
  walletSessions: [],
  consumerWallet: null,
  validBefore: '',
  paymentDetails: {
    amount: 0,
    currency: '',
    payauthzId: '',
    returnUrl: '',
    originalAmount: null,
    originalCurrency: null,
  },
  transactionDetails: {
    walletAddress: null,
    blockchainTxId: null,
    destinationAddress: null,
    destinationCurrency: null,
    destinationNetwork: null,
    destinationAmount: null,
    solanaLatestBlockhash: null,
    solanaDestinationTokenAccountAddress: null,
  },
  externalPaymentDetails: null,
  hasSucceededOrProcessingSetupAttempt: false,
  mandateDetails: {
    fpmandateId: '',
    returnUrl: '',
  },
  merchantBranding: {
    backgroundColor: MERCHANT_BRANDING_DEFAULTS.backgroundColor,
    buttonColor: MERCHANT_BRANDING_DEFAULTS.buttonColor,
    businessName: null,
    logoUrl: null,
  },

  // Other state
  screen: 'WALLET_SELECT',
  newWallet: null,
  exchangeOption: null,
  walletConnection: {
    ready: false,
    activeWallet: null,
    connectedWallets: [],
    walletKey: 'metamask',
    walletConnectQrCodeUri: null,
    device: 'desktop',
  },
  overlay: null,
  errorBanner: null,
  anyCryptoDeposit: {
    maxRelayRequestStatus: null,
    relayFailReason: null,
    relayRefundFailReason: null,
    relayFailureDetails: null,
    selectedToken: 'btc',
    selectedNetwork: null,
    fundingMethod: null,
    refundMethod: null,
    customRefundAddress: '',
    customerEmail: '',
    quote: {
      depositAddress: null,
      inputAmount: null,
      inputChain: null,
      inputCurrency: null,
    },
  },
  selectedCurrency: null,
};

const store = createStore(initialValues);

export const getStore = () => store;

export const {setValue, useSelector} = getStore()!;

export const resetAnyCryptoRefundState = (state: SessionState) => {
  state.anyCryptoDeposit.fundingMethod = null;
  state.anyCryptoDeposit.refundMethod = null;
  state.anyCryptoDeposit.customRefundAddress = '';
};
